import { useMutation, useQuery } from '@tanstack/react-query';
import { rolesCollection } from 'collections/roles';
import { fetchAllTeams, teamsCollection, useAllTeamsInAccountFromCollection } from 'collections/teams';
import { RoleType } from 'api/models';
import { TeamsAPI } from 'api/teams';
import { GENERAL_API_STALE } from 'config/query-config';
import { RolesApi } from 'data/roles';
import { useTranslation } from 'hooks/use-translation';
import { queryClient as queryClientDefault } from 'queries';
export const ROLES_QUERY_KEY = [
    'account_roles'
];
// Bridge: when tab-sync updates rolesCollection on this tab, reflect the change
// in the React Query cache so all useRolesQuery consumers re-render without a
// network round-trip.
rolesCollection.subscribeToWrites((event)=>{
    if (event.source !== 'tab-sync') return;
    queryClientDefault.setQueryData(ROLES_QUERY_KEY, rolesCollection.getAll());
});
export const ROLES_WITH_PLACEHOLDER_QUERY_KEY = [
    'account_roles_with_placeholder'
];
export const useCustomerRole = ()=>({
        roleId: -1,
        roleName: useTranslation('customer'),
        type: RoleType.Customer
    });
export const useRolesQuery = (queryOptions = {})=>useQuery(ROLES_QUERY_KEY, async ()=>{
        const roles = await RolesApi.findAll();
        rolesCollection.push(roles, {
            source: 'fetch'
        });
        const freshIds = new Set(roles.map((r)=>r.roleId));
        rolesCollection.getAll().filter((r)=>!freshIds.has(r.roleId)).forEach((r)=>rolesCollection.externalDelete(r.roleId));
        return roles;
    }, {
        staleTime: GENERAL_API_STALE,
        ...queryOptions
    });
export const useVendorRolesQuery = (queryOptions = {})=>useRolesQuery({
        ...queryOptions,
        select: (data)=>data.filter(({ type })=>type === RoleType.Vendor)
    });
export const useRolesWithPlaceholderQuery = (queryOptions = {})=>{
    const teams = useAllTeamsInAccountFromCollection();
    const roleIdsWithPlaceholder = teams.map((team)=>team === null || team === void 0 ? void 0 : team.roleId);
    return useQuery(ROLES_WITH_PLACEHOLDER_QUERY_KEY, async ()=>{
        const roles = await RolesApi.findAll();
        return roles.filter(({ roleId })=>roleIdsWithPlaceholder.includes(roleId));
    }, {
        staleTime: GENERAL_API_STALE,
        enabled: (roleIdsWithPlaceholder === null || roleIdsWithPlaceholder === void 0 ? void 0 : roleIdsWithPlaceholder.length) > 0,
        ...queryOptions
    });
};
export const useRolesWithPlaceholderQueryV2 = (queryOptions = {})=>{
    return useQuery(ROLES_WITH_PLACEHOLDER_QUERY_KEY, async ()=>{
        const roles = await RolesApi.findAll();
        const teams = await TeamsAPI.getAllTeamsInAccount();
        rolesCollection.push(roles, {
            source: 'fetch'
        });
        teamsCollection.push(teams, {
            source: 'fetch'
        });
        const roleIdsWithPlaceholder = teams.map((team)=>team === null || team === void 0 ? void 0 : team.roleId);
        return roles.filter(({ roleId })=>roleIdsWithPlaceholder.includes(roleId));
    }, {
        staleTime: GENERAL_API_STALE,
        ...queryOptions
    });
};
const createMutationFn = async ({ roleName, usedByPartners, type })=>{
    const role = await RolesApi.create({
        roleName,
        usedByPartners,
        type
    });
    rolesCollection.push([
        role
    ], {
        source: 'mutation'
    });
    return role;
};
const createWithDefaultPlaceholderMutationFn = async ({ roleName })=>{
    const role = await RolesApi.createWithDefaultPlaceholder({
        roleName
    });
    rolesCollection.push([
        role
    ], {
        source: 'mutation'
    });
    await fetchAllTeams();
    return role;
};
const updateMutationFn = async (id, { roleName, usedByPartners, type })=>{
    const role = await RolesApi.update(id, {
        roleName,
        usedByPartners,
        type
    });
    rolesCollection.push([
        role
    ], {
        source: 'mutation'
    });
    return role;
};
export const useCreateRoleMutation = (options = {})=>useMutation(createMutationFn, options);
export const useUpdateRoleMutation = (id, options = {})=>useMutation((formValues)=>updateMutationFn(id, formValues), options);
export const useDeleteRoleMutation = (id, options = {})=>useMutation(async ()=>{
        const result = await RolesApi.deleteRole(id);
        rolesCollection.externalDelete(id, {
            source: 'mutation'
        });
        return result;
    }, options);
export const useCreateRoleWithDefaultPlaceholderMutation = (options = {})=>useMutation(createWithDefaultPlaceholderMutationFn, options);
export const useMakeRolesAvailableForPartnersMutation = ()=>{
    return useMutation((roleIds)=>RolesApi.makeRolesAvailableForPartner(roleIds));
};
